import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAnnee } from '../hooks/useAnnee';
import { useDelai, useRessource } from '../hooks/useRessource';
import { BandeauMetriques } from '../components/BandeauMetriques';
import { BarreRecherche, type FiltreActif } from '../components/BarreRecherche';
import { ChoixMultiple } from '../components/ChoixMultiple';
import { ChampEtablissement } from '../components/ChampEtablissement';
import { estMineur } from '../lib/age';
import { bilanDroitImage, resumerDroitImage } from '../lib/droitImage';
import { CommunicationGroupee } from '../components/CommunicationGroupee';
import { dateExport, exporterTableau } from '../lib/export';
import { texteDepuisHtml } from '../lib/html';
import { IconeNote } from '../components/IconeNote';
import { ErreurApi, api } from '../lib/apiClient';
import {
  Badge,
  Champ,
  Chargement,
  EnTetePage,
  Erreur,
  Infobulle,
  Modale,
  Tableau,
  type Colonne,
} from '../components/Elements';
import { PiedFormulaire } from './ReferentielsPages';
import { ChampMembre, type SaisieMembre } from '../components/ChampMembre';
import { enregistrerMembre } from '../hooks/useAdhesion';
import { JOURS, formaterDate } from '../lib/format';
import type { Activite, Creneau, Personne, QualiteContact, SyntheseAdherents } from '../types';
import { ChampTelephone } from '../components/ChampTelephone';
import { formaterTelephone } from '../lib/telephone';

/**
 * Identite de l'adherent : nom, pastille de membre, age, liens.
 *
 * Tout tient sur UNE ligne. Les coordonnees et l'adresse ont leur propre
 * colonne : empilees dans cette cellule, elles rendaient la ligne de
 * tableau plus haute que les sept colonnes qu'elles remplacaient.
 */
function CelluleAdherent({ personne }: { personne: Personne }) {
  // RG-07 revisée : l'adhésion de l'année vaut statut. Une adhésion saisie
  // mais non couverte par un règlement reste signalée — c'est la nuance que
  // la pastille seule perdrait.
  const adhesionDecouverte =
    personne.membre_actif && (personne.adhesion_montant ?? 0) > (personne.adhesion_reglee ?? 0);

  return (
    <span className="flex items-center gap-x-2 whitespace-nowrap">
      {/* Un emplacement FIXE devant le nom (2026-10-31, demande de
          Georges) : il porte le bloc-notes quand la fiche a un
          commentaire, et reste vide sinon, pour que les noms s'alignent
          d'une ligne a l'autre. L'infobulle donne la note sans sa mise
          en forme. */}
      <span className="flex w-4 shrink-0 justify-center">
        {personne.commentaire && <IconeNote texte={texteDepuisHtml(personne.commentaire)} />}
      </span>
      <span className="font-medium">
        {personne.nom.toUpperCase()} {personne.prenom}
      </span>
      {personne.membre_actif && (
        <Infobulle
          texte={
            adhesionDecouverte
              ? 'Membre actif — adhésion non couverte par un règlement'
              : 'Membre actif'
          }
          className="flex shrink-0"
        >
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
        </Infobulle>
      )}
      {/* Un refus de droit a l'image se voit dans la liste (2026-10-13) :
          c'est avant un concert qu'on la parcourt. */}
      {bilanDroitImage(personne.droit_image) === 'refuse' && (
        <Infobulle
          texte={`Droit à l’image refusé — ${resumerDroitImage(personne.droit_image)}`}
          className="flex shrink-0"
        >
          <span className="h-2 w-2 rounded-full bg-red-500" />
        </Infobulle>
      )}
      {personne.age !== null && personne.age !== undefined && (
        <span className="text-xs text-encre-500">({personne.age} ans)</span>
      )}
      {adhesionDecouverte && (
        <Infobulle texte="Adhésion non couverte" className="text-xs text-amber-700">
          !
        </Infobulle>
      )}
      {/* Une famille n'existe qu'à partir de deux membres reliés :
          l'indication ne s'affiche donc que là où il y en a une. */}
      {(personne.nb_proches ?? 0) > 0 && (
        <Badge>
          {personne.nb_proches} lien{(personne.nb_proches ?? 0) > 1 ? 's' : ''}
        </Badge>
      )}
      {/* La colonne « Statut » a disparu : sans ce rappel, un inactif
          affiché via la case à cocher serait indiscernable des autres. */}
      {!personne.actif && <Badge ton="alerte">inactif</Badge>}
      {/* Meme tag que « reserve » sur le planning : liseré et texte
          bleus, fond blanc. Il dit qu'une inscription active de l'annee
          lue est un forfait d'essai (2026-09-29). */}
      {personne.forfait_essai && (
        <Infobulle
          texte={personne.forfait_essai_echu ? 'La fin de l’essai est passée : à confirmer, ou à arrêter.' : null}
          className={`shrink-0 rounded border bg-white px-1 text-[10px] font-medium leading-tight ${
            personne.forfait_essai_echu ? 'border-red-400 text-red-700' : 'border-blue-400 text-blue-700'
          }`}
        >
          {personne.forfait_essai_echu ? 'forfait d’essai terminé' : 'forfait d’essai'}
        </Infobulle>
      )}
    </span>
  );
}

/**
 * Enfants et adultes cote a cote, chacun avec son nombre et sa part de
 * la cohorte. Les fiches sans date de naissance ne sont ni l'un ni
 * l'autre : elles sont dites a part, sinon les deux parts ne feraient
 * pas cent.
 */
function RepartitionAges({
  mineurs,
  adultes,
  inconnus,
  part,
}: {
  mineurs: number;
  adultes: number;
  inconnus: number;
  part: (n: number) => string;
}) {
  // L'icone au-dessus, le nombre et sa part EN DESSOUS (2026-09-29) :
  // deux colonnes qu'on lit de haut en bas, comme deux pictogrammes.
  return (
    <div className="mt-2">
      <div className="flex items-start gap-8">
        <Infobulle texte="Moins de 18 ans" balise="div" className="flex flex-col items-center gap-1">
          <IconeEnfant />
          <span className="tabulaire whitespace-nowrap text-xs text-encre-600">
            <span className="font-semibold text-encre-900">{mineurs}</span> mineurs · {part(mineurs)}
          </span>
        </Infobulle>
        <Infobulle texte="18 ans et plus" balise="div" className="flex flex-col items-center gap-1">
          <IconeAdulte />
          <span className="tabulaire whitespace-nowrap text-xs text-encre-600">
            <span className="font-semibold text-encre-900">{adultes}</span> adultes · {part(adultes)}
          </span>
        </Infobulle>
      </div>
      {inconnus > 0 && (
        <div className="mt-1 text-xs text-encre-400">{inconnus} sans date de naissance</div>
      )}
    </div>
  );
}

/** Silhouette courte : un enfant. */
function IconeEnfant() {
  return (
    <svg viewBox="0 0 24 24" className="h-8 w-8 text-encre-400" fill="currentColor" aria-hidden>
      <circle cx="12" cy="8" r="3.2" />
      <path d="M12 12.5c-3 0-5 2-5 4.5V22h10v-5c0-2.5-2-4.5-5-4.5Z" />
    </svg>
  );
}

/** Silhouette haute : un adulte. */
function IconeAdulte() {
  return (
    <svg viewBox="0 0 24 24" className="h-8 w-8 text-encre-400" fill="currentColor" aria-hidden>
      <circle cx="12" cy="5" r="3" />
      <path d="M12 9c-3.6 0-6 2.4-6 5.5V22h12v-7.5C18 11.4 15.6 9 12 9Z" />
    </svg>
  );
}

/** L'ordre des series de contacts dans l'export, et leurs libelles. */
const ORDRE_QUALITES: QualiteContact[] = ['mere', 'pere', 'tuteur', 'conjoint', 'autre', 'eleve'];
const LIBELLE_QUALITE: Record<QualiteContact, string> = {
  mere: 'Mère',
  pere: 'Père',
  tuteur: 'Tuteur',
  conjoint: 'Conjoint',
  autre: 'Autre',
  eleve: 'Élève',
};

export function AdherentsPage() {
  const { peut, entiteActive } = useAuth();
  const naviguer = useNavigate();
  const [saisie, setSaisie] = useState('');
  const [modaleOuverte, setModaleOuverte] = useState(false);
  // « Ecrire aux familles » (2026-10-08) : les fiches AFFICHEES, gelees a l'ouverture.
  const [ecrireA, setEcrireA] = useState<string[] | null>(null);

  // Les quatre filtres multi-choix.
  const [jours, setJours] = useState<string[]>([]);
  const [activites, setActivites] = useState<string[]>([]);
  // PROFESSEUR (2026-10-08) : les adherents qu'un membre du personnel
  // encadre, sur la meme periode que « Jour ». Servi par l'API.
  const [professeurs, setProfesseurs] = useState<string[]>([]);

  // TYPE : usager et actif retenus d'office — on cherche presque
  // toujours quelqu'un que l'ecole connait cette annee. `null` signifie
  // « pas encore touche », comme pour l'annee ; tout decocher donne un
  // tableau VIDE, qui est un geste et ne retombe pas sur le defaut.
  const TYPES_PAR_DEFAUT = ['usager', 'actif'];
  const [typesChoisis, setTypesChoisis] = useState<string[] | null>(null);
  const types = typesChoisis ?? TYPES_PAR_DEFAUT;

  // FORFAIT D'ESSAI : pose par la tuile du meme nom, retire par sa
  // pastille. Un seul etat, vrai ou faux — il n'y a rien a choisir.
  const [essai, setEssai] = useState(false);

  // La liste se rafraichit a la frappe, apres une courte pause.
  const recherche = useDelai(saisie);

  // 500 est le plafond du serveur. En deca, la liste se coupait a 200 sans
  // le dire — pour 321 adherents, un tiers manquait en silence.
  const LIMITE = 500;

  // Les metriques ET les colonnes annuelles portent sur une annee : celle
  // du bandeau. Le filtre ci-dessous ne la remplace pas.
  const annee = useAnnee();

  // ANNEE DU FILTRE : la PERIODE sur laquelle « Jour » et « Type » se
  // lisent, pas une restriction de la liste. `null` signifie « pas encore
  // touche » — le filtre suit alors l'annee du bandeau, y compris quand
  // on en change. Des qu'on y touche, il vit sa vie.
  const [anneesChoisies, setAnneesChoisies] = useState<string[] | null>(null);
  const anneesFiltre = anneesChoisies ?? (annee.anneeId ? [annee.anneeId] : []);

  // La liste se charge D'EMBLEE, avec ses deux filtres pre-remplis :
  // l'annee du bandeau et le type « usager + actif ». C'est la vue que
  // l'on veut neuf fois sur dix — les gens que l'ecole connait cette
  // annee — et elle evite d'avoir a poser un geste pour voir quoi que ce
  // soit. Les 321 fiches completes restent a une case a cocher.
  //
  // `anneeId` part TOUJOURS : c'est lui qui calcule les activites suivies
  // et le statut de membre affiches en colonne. `annees`, lui, ne sert
  // qu'aux filtres — les deux ne se confondent pas.
  // Les memes parametres servent a la liste et a l'export, qui les
  // rejoue avec les contacts en plus.
  const parametresListe = {
    recherche,
    limite: LIMITE,
    anneeId: annee.anneeId,
    annees: anneesFiltre,
    jours,
    activites,
    professeurs,
    types,
    essai: essai || undefined,
  };
  const { donnees, chargement, erreur, recharger } = useRessource(
    () => api.get<Personne[]>('/api/personnes', parametresListe),
    [
      recherche,
      entiteActive?.id,
      annee.anneeId,
      anneesFiltre.join(),
      jours.join(),
      activites.join(),
      professeurs.join(),
      types.join(),
      essai,
    ],
  );

  // Les professeurs proposes : les ENCADRANTS des creneaux de l'annee du
  // bandeau — pas tout le registre du personnel (119 fiches), dont la
  // plupart n'ont aucun eleve.
  const referentielCreneaux = useRessource(
    () =>
      peut('creneau.lire') && annee.anneeId
        ? api.get<Creneau[]>('/api/creneaux', { anneeId: annee.anneeId })
        : Promise.resolve(null),
    [entiteActive?.id, annee.anneeId],
  );
  const optionsProfesseurs = (() => {
    const vus = new Map<string, string>();
    for (const c of referentielCreneaux.donnees ?? []) {
      for (const e of c.encadrants) {
        if (!vus.has(e.personnel_id)) vus.set(e.personnel_id, `${e.prenom_usuel} ${e.nom ?? ''}`.trim());
      }
    }
    return [...vus.entries()]
      .map(([valeur, libelle]) => ({ valeur, libelle }))
      .sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr'));
  })();

  // Les activites du REFERENTIEL, et non celles de la liste chargee :
  // sinon les options dependraient des filtres en cours, et cocher une
  // activite ferait disparaitre les autres du panneau.
  const referentielActivites = useRessource(
    () =>
      peut('activite.lire') && annee.anneeId
        ? api.get<Activite[]>('/api/activites', { anneeId: annee.anneeId })
        : Promise.resolve(null),
    [entiteActive?.id, annee.anneeId],
  );

  const synthese = useRessource(
    () =>
      annee.annee && peut('personne.lire')
        ? api.get<SyntheseAdherents>('/api/adherents/synthese', { anneeId: annee.anneeId })
        : Promise.resolve(null),
    [entiteActive?.id, annee.anneeId],
  );

  // L'activite est servie par l'API depuis le 2026-10-09, sur la MEME
  // inscription que le jour et le professeur. Filtree ici auparavant, sur
  // la colonne « Activites » : « piano + lundi » remontait un piano du
  // mercredi des que l'eleve avait un autre cours le lundi.
  const liste = donnees ?? [];

  const libelleDuJour = (valeur: string) => JOURS[Number(valeur)] ?? valeur;
  const LIBELLE_TYPE: Record<string, string> = {
    usager: 'Membre usager',
    actif: 'Membre actif',
    inactif: 'Inactif',
  };

  const filtresActifs: FiltreActif[] = [
    recherche && { cle: 'recherche', libelle: `« ${recherche} »`, onRetirer: () => setSaisie('') },

    // TOUS les filtres se montrent, defauts compris. Les masquer parce
    // qu'ils sont poses d'office donnait une barre qui annoncait
    // « 308 sur 321 » sans dire pourquoi : c'est justement le filtre
    // qu'on ne se rappelle pas avoir mis qu'il faut voir.
    //
    // Meme ordre que les listes deroulantes — activite, jour, annee,
    // type : on retrouve la pastille la ou l'oeil a pose le filtre.
    //
    // Retirer une pastille de defaut le transforme en choix explicite
    // (`setAnneesChoisies` / `setTypesChoisis` recoivent un tableau, plus
    // `null`), ce qui est bien ce que le geste veut dire.
    ...activites.map((a) => ({
      cle: `activite-${a}`,
      libelle: a,
      onRetirer: () => setActivites(activites.filter((v) => v !== a)),
    })),
    ...jours.map((j) => ({
      cle: `jour-${j}`,
      libelle: libelleDuJour(j),
      onRetirer: () => setJours(jours.filter((v) => v !== j)),
    })),
    ...professeurs.map((id) => ({
      cle: `prof-${id}`,
      libelle: optionsProfesseurs.find((o) => o.valeur === id)?.libelle ?? 'Professeur',
      onRetirer: () => setProfesseurs(professeurs.filter((v) => v !== id)),
    })),
    ...anneesFiltre.map((id) => ({
      cle: `annee-${id}`,
      libelle: annee.annees.find((a) => a.id === id)?.libelle ?? 'Année',
      onRetirer: () => setAnneesChoisies(anneesFiltre.filter((v) => v !== id)),
    })),
    ...types.map((t) => ({
      cle: `type-${t}`,
      libelle: LIBELLE_TYPE[t] ?? t,
      onRetirer: () => setTypesChoisis(types.filter((v) => v !== t)),
    })),
    essai && { cle: 'essai', libelle: 'Forfait d’essai', onRetirer: () => setEssai(false) },
  ].filter(Boolean) as FiltreActif[];

  const toutEffacer = () => {
    setSaisie('');
    setJours([]);
    setTypesChoisis(null);
    setActivites([]);
    setProfesseurs([]);
    setAnneesChoisies(null);
    setEssai(false);
  };

  // Part d'un compte dans la cohorte, en entier ; vide si la cohorte l'est.
  const part = (n: number) =>
    synthese.donnees && synthese.donnees.total > 0
      ? `${Math.round((n / synthese.donnees.total) * 100)} %`
      : '';

  // RG-43 : le Professeur reçoit une charge volontairement pauvre — ni
  // adresse, ni date de naissance. L'interface s'y adapte plutôt que
  // d'afficher des colonnes vides.
  const vueRestreinte = !peut('personne.modifier') && !peut('personne.creer');

  const colonnes: Colonne<Personne>[] = vueRestreinte
    ? [
        {
          cle: 'nom',
          entete: 'Élève',
          rendu: (p) => (
            <span className="font-medium">
              {p.nom.toUpperCase()} {p.prenom}
            </span>
          ),
        },
        { cle: 'tranche', entete: 'Tranche d’âge', rendu: (p) => p.tranche_age ?? '—' },
      ]
    : [
        {
          cle: 'adherent',
          entete: 'Adhérent',
          rendu: (p) => <CelluleAdherent personne={p} />,
        },
        {
          cle: 'coordonnees',
          entete: 'Coordonnées',
          // Une largeur maximale est indispensable : sans elle, `truncate`
          // n'a rien a quoi se mesurer et la colonne s'elargit au plus long
          // email de la liste.
          classe: 'max-w-[16rem]',
          rendu: (p) => {
            const coordonnees = [formaterTelephone(p.telephone, p.telephone_pays), p.email].filter(Boolean);
            return coordonnees.length === 0 ? (
              <span className="text-xs text-encre-400">—</span>
            ) : (
              <Infobulle texte={coordonnees.join(' · ')} balise="div" className="truncate text-xs text-encre-600">
                {coordonnees.join(' · ')}
              </Infobulle>
            );
          },
        },
        // La colonne Adresse a quitte la liste (2026-10-08) : elle
        // l'elargissait pour une information que la fiche porte, et que
        // l'export Excel garde.
        {
          cle: 'activites',
          entete: 'Activités',
          // Pas de `flex-wrap` : sous 1100 px les tags se repliaient et la
          // ligne doublait de hauteur. La carte defile horizontalement,
          // c'est la bonne reponse a l'etroitesse — pas l'empilement.
          rendu: (p) =>
            (p.activites ?? []).length === 0 ? (
              <span className="text-xs text-encre-400">—</span>
            ) : (
              <span className="flex gap-1 whitespace-nowrap">
                {p.activites!.map((activite) => (
                  <Badge key={activite}>{activite}</Badge>
                ))}
              </span>
            ),
        },
      ];

  return (
    <>
      <EnTetePage
        titre="Adhérents"
        description={
          vueRestreinte
            ? 'Vue restreinte : votre rôle donne accès au nom et à la tranche d’âge, sans montant, adresse ni date de naissance.'
            : undefined
        }
        actions={
          peut('personne.creer') ? (
            <button
              type="button"
              className="bouton-principal"
              onClick={() => setModaleOuverte(true)}
            >
              Nouvel adhérent
            </button>
          ) : null
        }
      />

      {synthese.donnees && !vueRestreinte && (
        <BandeauMetriques
          metriques={[
            // Le nombre est TOUTE la cohorte de l'annee — usagers et
            // membres actifs — et le detail dit combien cotisent
            // (2026-09-29). La tuile « Membres actifs » a disparu avec.
            {
              libelle: 'Adhérents',
              valeur: String(synthese.donnees.total),
              detail: `${synthese.donnees.membres_actifs} adhérents actifs`,
            },
            // Le titre filtre la liste sur place : les adherents dont une
            // inscription active porte le forfait d'essai.
            {
              libelle: 'Forfait d’essai',
              valeur: String(synthese.donnees.essais),
              // Les essais dont la fin est passee sans confirmation : ceux
              // qui appellent un geste (2026-10-08).
              detail:
                synthese.donnees.essais_echus > 0
                  ? `${synthese.donnees.essais_echus} terminé(s), à confirmer`
                  : undefined,
              action: () => setEssai(true),
            },
            {
              libelle: 'Répartition des âges',
              valeur: String(synthese.donnees.mineurs + synthese.donnees.adultes),
              contenu: (
                <RepartitionAges
                  mineurs={synthese.donnees.mineurs}
                  adultes={synthese.donnees.adultes}
                  inconnus={synthese.donnees.ages_inconnus}
                  part={part}
                />
              ),
            },
            {
              libelle: 'Fiches à compléter',
              valeur: String(synthese.donnees.fiches_a_completer),
              detail: 'date, adresse, contact',
              ton: 'alerte',
              lien: '/anomalies',
            },
          ]}
        />
      )}

      <BarreRecherche
        recherche={saisie}
        onChercher={setSaisie}
        placeholder="Nom, prénom, email, téléphone, adresse ou contact…"
        actifs={filtresActifs}
        onToutEffacer={toutEffacer}
        resume={
          donnees ? (
            <>
              <span className="tabulaire font-medium text-encre-800">{liste.length}</span>{' '}
              adhérent(s)
              {synthese.donnees ? ` sur ${synthese.donnees.total}` : ''}
              {donnees.length === LIMITE && (
                <span className="ml-2 text-amber-700">
                  — liste tronquée à {LIMITE}, affinez la recherche
                </span>
              )}
            </>
          ) : null
        }
        onEcrire={
          liste.length && peut('message.envoyer') ? () => setEcrireA(liste.map((p) => p.id)) : undefined
        }
        onExporter={
          liste.length
            ? async () => {
                // L'EXPORT REJOUE la liste avec ses contacts (2026-10-08) :
                // une serie de colonnes par QUALITE — Nom, Prenom, Email
                // (Mere), (Pere)... —, autant de series qu'une fiche en
                // compte au plus, pour que chaque colonne garde son sens
                // d'une ligne a l'autre. Une qualite absente de la liste
                // ne produit pas de colonne.
                const complet = await api.get<Personne[]>('/api/personnes', {
                  ...parametresListe,
                  avecContacts: true,
                });
                const maxParQualite = new Map<QualiteContact, number>();
                for (const p of complet) {
                  const compte = new Map<QualiteContact, number>();
                  for (const c of p.contacts_export ?? []) compte.set(c.qualite, (compte.get(c.qualite) ?? 0) + 1);
                  for (const [q, n] of compte) maxParQualite.set(q, Math.max(maxParQualite.get(q) ?? 0, n));
                }
                const series = ORDRE_QUALITES.flatMap((q) =>
                  Array.from({ length: maxParQualite.get(q) ?? 0 }, (_, i) => ({
                    qualite: q,
                    rang: i,
                    libelle: `${LIBELLE_QUALITE[q]}${(maxParQualite.get(q) ?? 0) > 1 ? ` ${i + 1}` : ''}`,
                  })),
                );
                exporterTableau(
                  'adherents',
                  [
                    'Nom',
                    'Prénom',
                    'Naissance',
                    'Tranche',
                    'Email',
                    'Téléphone',
                    'Adresse',
                    'Activités',
                    'Droit à l’image',
                    'Commentaire',
                    ...series.flatMap((s) => [`Nom (${s.libelle})`, `Prénom (${s.libelle})`, `Email (${s.libelle})`]),
                  ],
                  complet.map((p) => [
                    p.nom,
                    p.prenom,
                    dateExport(p.date_naissance),
                    p.tranche_age ?? '',
                    p.email ?? '',
                    formaterTelephone(p.telephone, p.telephone_pays),
                    [p.adresse_primaire, p.code_postal, p.ville].filter(Boolean).join(' '),
                    (p.activites ?? []).join(' · '),
                    resumerDroitImage(p.droit_image),
                    // Sans sa mise en forme : une cellule porte du texte.
                    texteDepuisHtml(p.commentaire),
                    ...series.flatMap((s) => {
                      const contact = (p.contacts_export ?? []).filter((c) => c.qualite === s.qualite)[s.rang];
                      return contact
                        ? [contact.nom ?? '', contact.prenom ?? '', contact.email ?? '']
                        : ['', '', ''];
                    }),
                  ]),
                );
              }
            : undefined
        }
        filtres={
          vueRestreinte ? null : (
            // CINQ filtres de MEME largeur (2026-10-08) : une grille a cinq
            // colonnes qui prend deux tiers de la barre, la recherche le
            // reste. Chaque liste remplit sa colonne. Le filtre « Droit a
            // l'image » y a vecu un jour (2026-10-13), retire a la demande
            // de Georges : l'API le sert toujours, l'export le porte.
            <div className="grid min-w-[40rem] flex-[2] grid-cols-5 gap-2">
              <ChoixMultiple
                classe="w-full"
                libelle="Activité"
                options={(referentielActivites.donnees ?? [])
                  .map((a) => ({ valeur: a.libelle, libelle: a.libelle }))
                  .sort((a, b) => a.libelle.localeCompare(b.libelle))}
                valeurs={activites}
                onChanger={setActivites}
              />

              <ChoixMultiple
                classe="w-full"
                libelle="Jour"
                options={[1, 2, 3, 4, 5, 6, 7].map((jour) => ({
                  valeur: String(jour),
                  libelle: JOURS[jour]!,
                }))}
                valeurs={jours}
                onChanger={setJours}
              />

              <ChoixMultiple
                classe="w-full"
                libelle="Professeur"
                options={optionsProfesseurs}
                valeurs={professeurs}
                onChanger={setProfesseurs}
              />

              <ChoixMultiple
                classe="w-full"
                libelle="Année scolaire"
                options={annee.annees.map((a) => ({ valeur: a.id, libelle: a.libelle }))}
                valeurs={anneesFiltre}
                onChanger={setAnneesChoisies}
              />

              <ChoixMultiple
                classe="w-full"
                libelle="Type"
                options={[
                  { valeur: 'usager', libelle: 'Membre usager' },
                  { valeur: 'actif', libelle: 'Membre actif' },
                  { valeur: 'inactif', libelle: 'Inactif' },
                ]}
                valeurs={types}
                onChanger={setTypesChoisis}
              />

            </div>
          )
        }
      />

      {erreur && <Erreur message={erreur} onReessayer={recharger} />}
      {chargement ? (
        <Chargement />
      ) : (
        <div className="carte">
          <Tableau
            colonnes={colonnes}
            lignes={liste}
            cleDe={(p) => p.id}
            messageVide="Aucun adhérent ne correspond."
            onLigneCliquee={vueRestreinte ? undefined : (p) => naviguer(`/adherents/${p.id}`)}
          />
        </div>
      )}

      <CommunicationGroupee
        ouverte={ecrireA !== null}
        onFermer={() => setEcrireA(null)}
        source={ecrireA ? { personneIds: ecrireA } : null}
        origine={
          ecrireA
            ? `Les ${ecrireA.length} adhérent(s) de la liste affichée, et leurs contacts — filtres : ${
                filtresActifs.map((f) => f.libelle).join(', ') || 'aucun'
              }.`
            : ''
        }
      />

      <FormulaireAdherent
        ouverte={modaleOuverte}
        onFermer={() => setModaleOuverte(false)}
        onEnregistre={(id) => {
          setModaleOuverte(false);
          naviguer(`/adherents/${id}`);
        }}
      />
    </>
  );
}

/** Une fiche existante au meme nom et prenom, telle que le serveur la renvoie avec le 409. */
interface Homonyme {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string | null;
  email: string | null;
  ville: string | null;
  adherent_entite: boolean;
}

/**
 * TEST D'EXISTENCE (2026-10-17) : le serveur refuse de creer une fiche
 * quand un homonyme existe, sans accents ni casse, et renvoie les fiches
 * trouvees. L'ecran les montre, avec un lien vers chacune, et ne cree
 * « quand meme » que sur un second geste explicite — quatre doublons
 * s'etaient glisses en base en trois semaines, le signalement apres
 * coup ne suffisait pas.
 */
function FormulaireAdherent({
  ouverte,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  onFermer: () => void;
  onEnregistre: (id: string) => void;
}) {
  const [valeurs, setValeurs] = useState({
    nom: '',
    prenom: '',
    dateNaissance: '',
    email: '',
    telephone: '',
    telephonePays: 'FR',
    ecoleFrequentee: '',
    civilite: '',
  });
  const [membre, setMembre] = useState<SaisieMembre>({ cotise: false });
  const [erreur, setErreur] = useState<string | null>(null);
  const [doublons, setDoublons] = useState<Homonyme[]>([]);
  const [enCours, setEnCours] = useState(false);
  // La cotisation se pose sur l'annee AFFICHEE, celle du bandeau : c'est
  // aussi l'annee ou l'inscription va suivre. Pas de chargement propre au
  // formulaire — c'en etait un qui rendait la case fausse sur la fiche.
  const annee = useAnnee();

  // Les homonymes ne valent que pour le nom et le prenom qui les ont
  // trouves : les corriger les efface, et le prochain envoi re-teste.
  const changer = (suite: Partial<typeof valeurs>) => {
    if ('nom' in suite || 'prenom' in suite) setDoublons([]);
    setValeurs({ ...valeurs, ...suite });
  };

  const soumettre = async (evenement: FormEvent | null, ignorerDoublon = false) => {
    evenement?.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const reponse = await api.post<{ personne: Personne }>('/api/personnes', {
        nom: valeurs.nom,
        prenom: valeurs.prenom,
        dateNaissance: valeurs.dateNaissance || null,
        email: valeurs.email || null,
        telephone: valeurs.telephone || null,
        telephonePays: valeurs.telephonePays,
        ecoleFrequentee: valeurs.ecoleFrequentee || null,
        civilite: valeurs.civilite || null,
        ignorerDoublon,
      });

      // L'adhesion suit la creation : elle a besoin de l'identifiant.
      await enregistrerMembre(
        reponse.personne.id,
        membre,
        { cotise: false, adhesionId: null },
        annee.annee
          ? { id: annee.annee.id, montantAdhesion: annee.annee.montant_adhesion }
          : null,
      );

      setDoublons([]);
      onEnregistre(reponse.personne.id);
    } catch (cause) {
      // Le 409 « doublon_personne » n'est pas une erreur : c'est la
      // question. Les fiches trouvees s'affichent, la creation attend.
      const homonymes =
        cause instanceof ErreurApi && cause.code === 'doublon_personne'
          ? ((cause.details as unknown as { doublons?: Homonyme[] } | undefined)?.doublons ?? [])
          : [];
      if (homonymes.length > 0) setDoublons(homonymes);
      else setErreur(cause instanceof ErreurApi ? cause.message : 'Création impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Nouvel adhérent" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Nom">
            <input
              className="champ uppercase"
              value={valeurs.nom}
              onChange={(e) => changer({ nom: e.target.value })}
              required
              autoFocus
            />
          </Champ>
          <Champ libelle="Prénom">
            <input
              className="champ"
              value={valeurs.prenom}
              onChange={(e) => changer({ prenom: e.target.value })}
              required
            />
          </Champ>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Date de naissance">
            <input
              className="champ"
              type="date"
              value={valeurs.dateNaissance}
              onChange={(e) => setValeurs({ ...valeurs, dateNaissance: e.target.value })}
            />
          </Champ>
          <Champ libelle="Civilité" aide="Sert à la pyramide des âges du dashboard. Facultative.">
            <select
              className="champ"
              value={valeurs.civilite}
              onChange={(e) => setValeurs({ ...valeurs, civilite: e.target.value })}
            >
              <option value="">Non renseignée</option>
              <option value="M">M.</option>
              <option value="Mme">Mme</option>
            </select>
          </Champ>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Email" aide="Celui de l’adhérent lui-même. Facultatif.">
            <input
              className="champ"
              type="email"
              value={valeurs.email}
              onChange={(e) => setValeurs({ ...valeurs, email: e.target.value })}
            />
          </Champ>
          <ChampTelephone
            aide="Celui de l’adhérent lui-même. Facultatif."
            numero={valeurs.telephone}
            pays={valeurs.telephonePays}
            onChange={(telephone, telephonePays) =>
              setValeurs({ ...valeurs, telephone, telephonePays })
            }
          />
        </div>

        {/* Meme regle que la fiche (2026-10-13) : sous les coordonnees,
            masquee pour les adultes. */}
        {estMineur(valeurs.dateNaissance) !== false && (
          <ChampEtablissement
            valeur={valeurs.ecoleFrequentee}
            onChanger={(ecoleFrequentee) => setValeurs({ ...valeurs, ecoleFrequentee })}
          />
        )}

        <ChampMembre
          valeur={membre}
          onChanger={setMembre}
          anneeLibelle={annee.annee?.libelle ?? null}
          montantAdhesion={annee.annee?.montant_adhesion ?? null}
        />

        <PiedFormulaire
          erreur={erreur}
          enCours={enCours}
          onFermer={onFermer}
          libelle="Créer"
          enfants={
            doublons.length > 0 ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">
                  {doublons.length === 1
                    ? 'Une fiche existe déjà à ce nom et ce prénom.'
                    : `${doublons.length} fiches existent déjà à ce nom et ce prénom.`}
                </p>
                <ul className="mt-1 list-inside list-disc">
                  {doublons.map((doublon) => (
                    <li key={doublon.id}>
                      <Link to={`/adherents/${doublon.id}`} className="underline">
                        {doublon.nom} {doublon.prenom}
                      </Link>
                      {doublon.date_naissance
                        ? ` — né(e) le ${formaterDate(doublon.date_naissance)}`
                        : ' — date de naissance inconnue'}
                      {doublon.ville ? ` · ${doublon.ville}` : ''}
                      {doublon.email ? ` · ${doublon.email}` : ''}
                      {doublon.adherent_entite ? ' · inscrit(e) cette année' : ''}
                    </li>
                  ))}
                </ul>
                <p className="mt-2">
                  Ouvrez la fiche existante plutôt que d’en créer une seconde. S’il s’agit bien
                  d’une autre personne :{' '}
                  <button
                    type="button"
                    className="underline"
                    disabled={enCours}
                    onClick={() => void soumettre(null, true)}
                  >
                    créer quand même
                  </button>
                  .
                </p>
              </div>
            ) : null
          }
        />
      </form>
    </Modale>
  );
}
